import { HeroStyle2 } from "~/components/HeroStyle2";
import {
  Locale,
  type LocalizedStrings,
  useLocalization,
} from "~/components/localization/LocalizationProvider";
import { Box, Flex, Heading, Text } from "@radix-ui/themes";
import styles from "./our-treatments.module.css";
import { HomeConsultForm } from "~/components/home/HomeConsultForm";
import { HoverableImageLink } from "~/components/about-us/HoverableImageLink";

type Localization = {
  title: string;
  heading: string;
  headingContent: string;
  smileTreatmentHeading: string;
  smileTreatmentP1: string;
  smileTreatmentList: string[];
  metalBracesHeading: string;
  metalBracesP1: string;
  metalBracesP2: string;
  fixedVsRemovableHeading: string;
  fixedVsRemovableP1: string;
  fixedVsRemovableP2: string;
  fixedVsRemovableP3: string;
  invisalign: string;
  invisalignForTeens: string;
  metalBraces: string;
  clearBraces: string;
  orthoForAllAges: string;
  orthoInfo: string;
};

const englishLocalization: Localization = {
  title: "Our Treatments",
  heading: "You've Got Treatment Options",
  headingContent:
    "You can get the newest orthodontic treatments at Miami Orthodontic Specialists to make your new smile fast and affordable.",
  smileTreatmentHeading: "Smile Treatment",
  smileTreatmentP1:
    "By pairing state-of-the-art technology with her thorough understanding of the movement and growth of the teeth and jaws, Dr. Garcia-Paul is able to successfully treat many dental issues, like:",
  smileTreatmentList: [
    "Crowded teeth",
    "Gaps between teeth",
    "Protruding teeth",
    "Overbite",
    "Underbite",
    "Crossbite",
    "Impacted teeth",
  ],
  metalBracesHeading: "Metal Braces & More",
  metalBracesP1:
    "Most patients we treat choose traditional metal braces to straighten their teeth. While metal braces are the most common type of orthodontic treatment at Miami Orthodontic Specialists, you have several other options.",
  metalBracesP2:
    "After your first free consultation, Dr. Garcia-Paul will talk to you about which treatment would be right for you based on your unique needs and smile goals.",
  fixedVsRemovableHeading: "Fixed vs. Removable Appliances",
  fixedVsRemovableP1:
    "It's important to understand the difference between fixed and removable appliances. Fixed appliances, like braces, are attached to your teeth and aren't taken off until you complete your treatment at Miami Orthodontic Specialists.",
  fixedVsRemovableP2:
    "Removable appliances, like Invisalign's aligner system, are worn about 20-22 hours a day but can be taken off easily. Aside from the obvious differences, Invisalign aligners are best at treating mild to moderate orthodontic problems.",
  fixedVsRemovableP3:
    "Fixed appliances are generally needed for more serious and comprehensive treatment.",
  invisalign: "Invisalign",
  invisalignForTeens: "Invisalign for Teens",
  metalBraces: "Metal Braces",
  clearBraces: "Clear Braces",
  orthoForAllAges: "Ortho for All Ages",
  orthoInfo: "Ortho Info",
};

const spanishLocalization: Localization = {
  title: "Nuestros tratamientos",
  heading: "Tienes opciones de tratamiento",
  headingContent:
    "Puedes obtener los tratamientos ortodónticos más nuevos en Miami Orthodontic Specialists para hacer que tu nueva sonrisa sea rápida y asequible.",
  smileTreatmentHeading: "Tratamiento de la sonrisa",
  smileTreatmentP1:
    "Al combinar tecnología de última generación con su profundo conocimiento del movimiento y el crecimiento de los dientes y las mandíbulas, la Dra. García-Paul puede tratar con éxito muchos problemas dentales, como:",
  smileTreatmentList: [
    "Dientes apiñados",
    "Espacios entre los dientes",
    "Dientes que sobresalen",
    "Sobremordida",
    "Mordida invertida",
    "Mordida cruzada",
    "Dientes impactados",
  ],
  metalBracesHeading: "Brackets metálicos y más",
  metalBracesP1:
    "La mayoría de los pacientes que tratamos eligen los aparatos de ortodoncia tradicionales de metal para enderezar sus dientes. Si bien los aparatos de ortodoncia de metal son el tipo de tratamiento de ortodoncia más común en Miami Orthodontic Specialists, existen otras opciones.",
  metalBracesP2:
    "Después de su primera consulta gratuita, el Dr. García-Paul hablará con usted sobre qué tratamiento sería el adecuado para usted en función de sus necesidades únicas y sus objetivos de sonrisa.",
  fixedVsRemovableHeading: "Dispositivos fijos y extraíbles",
  fixedVsRemovableP1:
    "Es importante entender la diferencia entre aparatos fijos y removibles. Los aparatos fijos, como los brackets, se adhieren a los dientes y no se quitan hasta que finaliza el tratamiento en Miami Orthodontic Specialists.",
  fixedVsRemovableP2:
    "Los aparatos removibles, como el sistema de alineadores Invisalign, se usan entre 20 y 22 horas al día, pero se pueden quitar fácilmente. Aparte de las diferencias obvias, los alineadores Invisalign son los mejores para tratar problemas de ortodoncia leves a moderados.",
  fixedVsRemovableP3:
    "Generalmente se necesitan aparatos fijos para tratamientos más serios y completos.",
  invisalign: "Invisalign",
  invisalignForTeens: "Invisalign para adolescentes",
  metalBraces: "Brackets de metal",
  clearBraces: "Brackets transparentes",
  orthoForAllAges: "Ortodoncia para todas las edades",
  orthoInfo: "Información de ortodoncia",
};

const localized: LocalizedStrings<Localization> = {
  [Locale.EN]: englishLocalization,
  [Locale.ES]: spanishLocalization,
};

export function OurTreatmentsPage() {
  const getLocalization = useLocalization(localized);
  return (
    <>
      <HeroStyle2
        heroImageUrl="/our-treatments-hero.jpg"
        title={getLocalization("title")}
        backgroundPosition="center"
      />
      <Flex
        className={`section ${styles.headingSection}`}
        width="100%"
        align="center"
        justify="center"
      >
        <Flex direction="column" gap="7" className={`innerSection`} p="5">
          <Heading as="h2" className={styles.heading} weight="bold">
            {getLocalization("heading")}
          </Heading>
          <Text
            size="5"
            style={{
              lineHeight: "1.55em",
              letterSpacing: "normal",
              textWrap: "pretty",
            }}
          >
            {getLocalization("headingContent")}
          </Text>
        </Flex>
      </Flex>
      <Flex
        className={`section ${styles.mainContentSectionOuter}`}
        width="100%"
        align="center"
        justify="center"
      >
        <Flex
          className={styles.contentSection}
          gap="5"
          direction="row"
          wrap="wrap"
          width="100%"
          justify="center"
          px="4"
        >
          <Flex
            flexBasis={{ initial: "100%", sm: "47%" }}
            gap="5"
            direction="column"
            minWidth="40%"
          >
            <Heading as="h3" className={styles.subsectionHeading}>
              {getLocalization("smileTreatmentHeading")}
            </Heading>
            <Text size="5">{getLocalization("smileTreatmentP1")}</Text>
            <Text size="5">
              {
                <Flex asChild direction="column" gap="2">
                  <ul>
                    {getLocalization("smileTreatmentList").map(
                      (item) => <li key={item}>{item}</li>
                    )}
                  </ul>
                </Flex>
              }
            </Text>
          </Flex>
          <Flex
            flexBasis={{ initial: "100%", sm: "47%" }}
            gap="5"
            direction="column"
            minWidth="40%"
          >
            <Heading as="h3" className={styles.subsectionHeading}>
              {getLocalization("metalBracesHeading")}
            </Heading>
            <Text size="5">{getLocalization("metalBracesP1")}</Text>
            <Text size="5">{getLocalization("metalBracesP2")}</Text>
            <Heading as="h3" className={styles.subsectionHeading}>
              {getLocalization("fixedVsRemovableHeading")}
            </Heading>
            <Text size="5">{getLocalization("fixedVsRemovableP1")}</Text>
            <Text size="5">{getLocalization("fixedVsRemovableP2")}</Text>
            <Text size="5">{getLocalization("fixedVsRemovableP3")}</Text>
          </Flex>
        </Flex>
      </Flex>
      <Flex
        className={`section ${styles.secondaryContentSectionOuter}`}
        width="100%"
        justify="center"
        align="center"
      >
        <Flex
          gap="7"
          direction="column"
          width="100%"
          px="4"
          className={styles.contentSection}
        >
          <Flex
            wrap="wrap"
            width="100%"
            gap="4"
            justify={{ initial: "center", md: "between" }}
            direction={{ initial: "column", md: "row" }}
          >
            <Box flexBasis={{ initial: "0", md: "49%" }}>
              <HoverableImageLink
                imageUrl="/invisalign.jpg"
                label={getLocalization("invisalign")}
                to="/invisalign"
              />
            </Box>
            <Box flexBasis={{ initial: "0", md: "49%" }}>
              <HoverableImageLink
                imageUrl="/invisalign-for-teens.jpg"
                label={getLocalization("invisalignForTeens")}
                to="/invisalign-for-teens"
              />
            </Box>
            <Box flexBasis={{ initial: "0", md: "49%" }}>
              <HoverableImageLink
                imageUrl="/metal-braces.jpg"
                label={getLocalization("metalBraces")}
                to="/metal-braces"
              />
            </Box>
            <Box flexBasis={{ initial: "0", md: "49%" }}>
              <HoverableImageLink
                imageUrl="/clear-braces.jpg"
                label={getLocalization("clearBraces")}
                to="/clear-braces"
              />
            </Box>
            <Box flexBasis={{ initial: "0", md: "49%" }}>
              <HoverableImageLink
                imageUrl="/ortho-for-all-ages.jpg"
                label={getLocalization("orthoForAllAges")}
                to="/orthodontics-for-all-ages"
              />
            </Box>
            <Box flexBasis={{ initial: "0", md: "49%" }}>
              <HoverableImageLink
                imageUrl="/ortho-info.jpg"
                label={getLocalization("orthoInfo")}
                to="/ortho-info"
              />
            </Box>
          </Flex>
        </Flex>
      </Flex>
      <Box className={styles.form}>
        <HomeConsultForm />
      </Box>
    </>
  );
}
